import { hasAccess } from './hasAccess';

/**
 * Single source of truth for dashboard nav items.
 * Navbar and sidebar keep their original groups and order.
 * Both render from this file. Do not add items in the navbar or sidebar.
 * Filter every leaf with hasAccess.
 */

export const NAV_HOME = {
  title: 'Home',
  path: '/dashboard/home',
  icon: 'home',
};

export const NAV_ACCOUNT = {
  title: 'Manage Account',
  path: '/dashboard/account',
  icon: 'person',
};

const leaf = (title, path, icon, accessKey, extra = {}) => ({
  title,
  path,
  icon,
  accessKey,
  ...extra,
});

const purchases = leaf('Purchases', '/dashboard/purchases', 'shoppingCart', 'purchases');
const customers = leaf('Customers', '/dashboard/customers', 'people', 'customers');
const prescriptionManagement = leaf(
  'Prescription Management',
  '/dashboard/customers/upcoming-nax-refills',
  'medical',
  'prescriptionManagement'
);
const connect = leaf('Connect', '/dashboard/connect', 'connect', 'connect');
const katSubscriptionMgmt = leaf(
  'KAT Subscription Mgmt',
  '/dashboard/subscription-management',
  'creditCard',
  'katSubscriptionMgmt'
);
const scheduledActions = leaf(
  'Scheduled Actions',
  '/dashboard/scheduled-actions',
  'schedule',
  'scheduledActions'
);
const consultationWaitlist = leaf(
  'Consultation Waitlist',
  '/dashboard/consultation-waitlist',
  'queue',
  'consultationWaitlist'
);
const stuckConsultations = leaf(
  'Stuck Consultations',
  '/dashboard/stuck-consultations',
  'warning',
  'stuckConsultations'
);
const testKits = leaf('Test Kits', '/dashboard/test-kits', 'medical', 'testKits');
const doctorRequests = leaf('Doctor Requests', '/dashboard/doctor-requests', 'hospital', 'doctorRequests');
const linkShortener = leaf('Link Shortener', '/dashboard/link-shortener', 'link', 'linkShortener');
const knowledgeBase = leaf('Knowledge Base', '/dashboard/knowledge-base', 'menuBook', 'knowledgeBase');
const orders = leaf('Orders', '/dashboard/orders', 'creditCard', 'orders');

const myChats = leaf(
  'My Chats',
  '/dashboard/chats?chatType=consultation&queue=mine',
  'chat',
  'myWorkQueues'
);
const consult = leaf(
  'Consult',
  '/dashboard/chats?chatType=consultation',
  'chat',
  'chats',
  { badge: 'consultations' }
);
const consultationChats = leaf(
  'Consultation Chats',
  '/dashboard/chats?chatType=consultation',
  'chat',
  'chats',
  { badge: 'consultations' }
);
const support = leaf(
  'Support',
  '/dashboard/chats?chatType=support',
  'chat',
  'supportChats',
  { badge: 'support' }
);
const supportChats = leaf(
  'Support Chats',
  '/dashboard/chats?chatType=support',
  'chat',
  'supportChats',
  { badge: 'support' }
);
const coachingChatShort = leaf(
  'Coaching',
  '/dashboard/chats?chatType=coaching',
  'chat',
  'coachingChats',
  { badge: 'coaching' }
);
const coachingChats = leaf(
  'Coaching Chats',
  '/dashboard/chats?chatType=coaching',
  'chat',
  'coachingChats',
  { badge: 'coaching' }
);
const metrics = leaf('Metrics', '/dashboard/customer-service', 'queryStats', 'customerServiceDashboard');
const configs = leaf('Configs', '/dashboard/chat-configs', 'settings', 'chatConfigs');
const chatTools = leaf('Tools', '/dashboard/chats/tools', 'science', 'chatTools');
const chatToolsLong = leaf('Chat Tools', '/dashboard/chats/tools', 'science', 'chatTools');
const chatConfigs = leaf('Chat Configs', '/dashboard/chat-configs', 'settings', 'chatConfigs');
const checkInHolds = leaf(
  'Check-in holds',
  '/dashboard/clinician-chat-holds',
  'eventBusy',
  'clinicianChatHolds'
);
const clinicianCheckInHolds = leaf(
  'Clinician check-in holds',
  '/dashboard/clinician-chat-holds',
  'eventBusy',
  'clinicianChatHolds'
);
const aurora = leaf('Aurora', '/dashboard/aurora-audit', 'chat', 'auroraAudit');

const myConsultations = leaf(
  'My Consultations',
  '/dashboard/consultations?queue=mine',
  'hospital',
  'myWorkQueues'
);
const consultations = leaf(
  'Consultations',
  '/dashboard/consultations?queue=all',
  'hospital',
  'consultations'
);
const insuranceConsults = leaf(
  'Insurance Consults',
  '/dashboard/customers/upcoming-insurance-consults',
  'hospital',
  'insuranceConsults'
);

const liveSales = leaf('Live Sales', '/dashboard/live-sales', 'trending', 'liveSales');
const salesDashboard = leaf('Sales Dashboard', '/dashboard/sales', 'monetization', 'salesDashboard');
const formSubmissions = leaf('Form Submissions', '/dashboard/form-submissions', 'feedback', 'formSubmissions');
const p2pReferrals = leaf('P2P Referrals', '/dashboard/p2p-referrals', 'people', 'p2pReferrals');
const testimonialsReview = leaf(
  'Testimonials Review & Library',
  '/dashboard/testimonials/review',
  'feedback',
  'testimonials'
);
const testimonialInvites = leaf(
  'Testimonial Invites',
  '/dashboard/testimonials/invites',
  'feedback',
  'testimonials'
);
const contentRequests = leaf(
  'Content Requests',
  '/dashboard/content-capture/requests',
  'videocam',
  'contentCapture'
);
const contentLibrary = leaf(
  'Content Library',
  '/dashboard/content-capture/library',
  'videocam',
  'contentCapture'
);
const myRecordingRequests = leaf(
  'My recording requests',
  '/dashboard/content-capture/my',
  'videocam',
  'contentCaptureMy'
);
const videoTestimonialsGroup = {
  title: 'Video Testimonials',
  path: '/dashboard/testimonials',
  icon: 'feedback',
  children: [
    leaf('Review & Library', '/dashboard/testimonials/review', 'feedback', 'testimonials'),
    leaf('Invites', '/dashboard/testimonials/invites', 'feedback', 'testimonials'),
  ],
};
const contentCaptureGroup = {
  title: 'Content Capture',
  path: '/dashboard/content-capture',
  icon: 'videocam',
  children: [
    leaf('Requests', '/dashboard/content-capture/requests', 'videocam', 'contentCapture'),
    leaf('Library', '/dashboard/content-capture/library', 'videocam', 'contentCapture'),
    myRecordingRequests,
  ],
};

const iopLeads = leaf('IOP Leads', '/dashboard/iop/leads', 'hospital', 'iopLeads');
const iopEpisodes = leaf('IOP Episodes', '/dashboard/iop/episodes', 'assignment', 'iopLeads');
const iopCohorts = leaf('IOP Cohorts', '/dashboard/iop/cohorts', 'people', 'iopLeads');
const iopChats = leaf('IOP Chats', '/dashboard/chats?chatType=iop', 'chat', 'iopChats', { badge: 'iop' });

const contentCreation = leaf(
  'Content Creation',
  '/dashboard/content-creation',
  'autoStories',
  'contentCreation'
);
const icebergPatients = leaf('Iceberg Patients', '/dashboard/iceberg-patients', 'queue', 'icebergPatients');
const coachingPatients = leaf('Coaching Patients', '/dashboard/coaching/patients', 'people', 'coachingPatients');
const coachingSessions = leaf(
  'Coaching Sessions',
  '/dashboard/coaching/sessions',
  'eventNote',
  'coachingSessions'
);
const groupSessions = leaf('Group Sessions', '/dashboard/group-sessions', 'groups', 'groupSessions');

const adminUsers = leaf('Admin Users', '/dashboard/admin-users', 'users', 'adminUsers');
const brainRoutines = leaf('Brain Routines', '/dashboard/brain-routines', 'schedule', 'brainRoutines');
const funnelBrowser = leaf('Funnel Browser', '/dashboard/funnel-browser', 'science', 'funnelBrowser');
const emailTester = leaf('Email Tester', '/dashboard/email-tester', 'email', 'emailTester');
const clinicians = leaf('Clinicians', '/dashboard/clinicians', 'hospital', 'clinicians');
const supportedStates = leaf('Supported States', '/dashboard/supported-states', 'hospital', 'supportedStates');
const planManagement = leaf('Plan management', '/dashboard/nmi/plans', 'creditCard', 'planManagement');
const adminReports = leaf('Admin Reports', '/dashboard/admin', 'feedback', 'adminReports');
const prescriptionBalance = leaf(
  'Prescription Balance',
  '/dashboard/prescription-balance',
  'medical',
  'prescriptionBalance'
);

/** Desktop dropdowns. Labels and order match the pre-restructure navbar. */
export const NAVBAR_GROUPS = [
  {
    id: 'main',
    title: 'Main',
    items: [
      purchases,
      customers,
      prescriptionManagement,
      connect,
      katSubscriptionMgmt,
      scheduledActions,
      consultationWaitlist,
      stuckConsultations,
      testKits,
      doctorRequests,
      linkShortener,
      knowledgeBase,
      orders,
    ],
  },
  {
    id: 'chat',
    title: 'Chat',
    showWaitingTotal: true,
    items: [
      myChats,
      consult,
      support,
      coachingChatShort,
      metrics,
      configs,
      checkInHolds,
      chatTools,
      aurora,
    ],
  },
  {
    id: 'clinicians',
    title: 'Clinicians',
    items: [
      myConsultations,
      myChats,
      consultations,
      insuranceConsults,
    ],
  },
  {
    id: 'sales',
    title: 'Sales/Marketing',
    items: [
      liveSales,
      salesDashboard,
      formSubmissions,
      p2pReferrals,
      funnelBrowser,
    ],
  },
  {
    id: 'iop',
    title: 'IOP',
    items: [
      iopLeads,
      iopEpisodes,
      iopCohorts,
      iopChats,
    ],
  },
  {
    id: 'coaching',
    title: 'Coaching',
    items: [
      contentCreation,
      coachingChats,
      icebergPatients,
      coachingPatients,
      coachingSessions,
      groupSessions,
    ],
  },
  {
    id: 'admin',
    title: 'Admin',
    items: [
      adminUsers,
      brainRoutines,
      emailTester,
      clinicians,
      supportedStates,
      planManagement,
      adminReports,
      prescriptionBalance,
      testimonialsReview,
      testimonialInvites,
      contentRequests,
      contentLibrary,
      myRecordingRequests,
    ],
  },
];

/** Mobile drawer sections. Labels and order match the pre-restructure sidebar. */
export const SIDEBAR_GROUPS = [
  {
    id: 'ops',
    title: 'Ops',
    items: [
      purchases,
      customers,
      myChats,
      consultationChats,
      supportChats,
      metrics,
      chatConfigs,
      clinicianCheckInHolds,
      chatToolsLong,
      coachingChats,
      aurora,
      prescriptionManagement,
      connect,
      katSubscriptionMgmt,
      testKits,
      doctorRequests,
      stuckConsultations,
      linkShortener,
      knowledgeBase,
      orders,
    ],
  },
  {
    id: 'clinicians',
    title: 'Clinicians',
    items: [
      myConsultations,
      myChats,
      consultations,
      insuranceConsults,
    ],
  },
  {
    id: 'sales',
    title: 'Sales/Marketing',
    items: [
      liveSales,
      salesDashboard,
      p2pReferrals,
      formSubmissions,
      funnelBrowser,
      {
        title: 'IOP',
        path: '/dashboard/iop',
        icon: 'hospital',
        children: [
          leaf('Leads', '/dashboard/iop/leads', 'hospital', 'iopLeads'),
          leaf('Episodes', '/dashboard/iop/episodes', 'assignment', 'iopLeads'),
          leaf('Cohorts', '/dashboard/iop/cohorts', 'people', 'iopLeads'),
          leaf('Chats', '/dashboard/chats?chatType=iop', 'chat', 'iopChats', { badge: 'iop' }),
        ],
      },
    ],
  },
  {
    id: 'coaching',
    title: 'Coaching',
    items: [
      contentCreation,
      icebergPatients,
      coachingPatients,
      coachingSessions,
      groupSessions,
    ],
  },
  {
    id: 'admin',
    title: 'Admin',
    items: [
      adminUsers,
      brainRoutines,
      emailTester,
      clinicians,
      supportedStates,
      adminReports,
      planManagement,
      prescriptionBalance,
      scheduledActions,
      consultationWaitlist,
      videoTestimonialsGroup,
      contentCaptureGroup,
    ],
  },
];

/** Alias for the desktop tree. Prefer NAVBAR_GROUPS / SIDEBAR_GROUPS when adding items. */
export const NAV_GROUPS = NAVBAR_GROUPS;

export function filterNavItems(items, userRoles, context = {}) {
  if (!items) return [];
  return items.reduce((acc, item) => {
    if (item.children && item.children.length) {
      const children = filterNavItems(item.children, userRoles, context);
      if (children.length === 0) return acc;
      acc.push({ ...item, children });
      return acc;
    }
    if (hasAccess(item.accessKey, userRoles, context)) {
      acc.push(item);
    }
    return acc;
  }, []);
}

export function flattenNavItems(items) {
  const out = [];
  (items || []).forEach((item) => {
    if (item.children && item.children.length) {
      out.push(...flattenNavItems(item.children));
    } else if (item.path) {
      out.push(item);
    }
  });
  return out;
}

export function getVisibleNavGroups(groups, userRoles, context = {}) {
  return (groups || [])
    .map((group) => ({
      ...group,
      items: filterNavItems(group.items, userRoles, context),
    }))
    .filter((group) => group.items.length > 0);
}

/** Navbar: original dropdowns with children flattened to leaves. */
export function buildNavbarMenus(userRoles, context = {}) {
  return getVisibleNavGroups(NAVBAR_GROUPS, userRoles, context).map((group) => ({
    ...group,
    items: flattenNavItems(group.items),
  }));
}

/** Sidebar: Home plus the original sections, children still nested. */
export function buildSidebarSections(userRoles, context = {}) {
  const groups = getVisibleNavGroups(SIDEBAR_GROUPS, userRoles, context);
  return [
    {
      id: 'home',
      title: 'Navigation',
      items: [NAV_HOME],
    },
    ...groups,
  ];
}

const ALL_GROUP_TREES = [...NAVBAR_GROUPS, ...SIDEBAR_GROUPS];

export function collectNavPaths(groups = ALL_GROUP_TREES, extras = [NAV_HOME, NAV_ACCOUNT]) {
  const paths = extras.filter((item) => item.path).map((item) => item.path);
  const walk = (items) => {
    (items || []).forEach((item) => {
      if (item.path) paths.push(item.path);
      if (item.children) walk(item.children);
    });
  };
  (groups || []).forEach((group) => walk(group.items));
  return [...new Set(paths)];
}

export function collectNavLeaves(groups = ALL_GROUP_TREES) {
  return flattenNavItems((groups || []).flatMap((group) => group.items || []));
}

export function findNavItemByPath(path, groups = ALL_GROUP_TREES) {
  let found = null;
  const walk = (items) => {
    (items || []).forEach((item) => {
      if (item.path === path && !found) found = item;
      if (item.children) walk(item.children);
    });
  };
  (groups || []).forEach((group) => walk(group.items));
  if (NAV_HOME.path === path) return NAV_HOME;
  if (NAV_ACCOUNT.path === path) return NAV_ACCOUNT;
  return found;
}
